<template>
    <section class="personal-records-highlight">
        <h2 class="personal-records-highlight__title">Лучший матч</h2>

        <div v-if="currentRecord" class="record-highlight-card">
            <div class="record-highlight-card__value">
                <span class="record-value">{{ displayValue }}</span>
                <span class="record-metric-label">{{ metricLabel }}</span>
            </div>
            <div class="record-highlight-card__match">
                <span class="record-date">{{ formatDate(currentRecord.date) }}</span>
                <div class="record-teams">
                    <span class="record-team">{{ currentRecord.home_team_short || currentRecord.home_team }}</span>
                    <span class="record-vs">—</span>
                    <span class="record-team">{{ currentRecord.away_team_short || currentRecord.away_team }}</span>
                </div>
            </div>
            <button type="button" class="record-change-btn" @click="showModal = true">
                Другой показатель
            </button>
        </div>

        <div v-else class="record-highlight-card record-highlight-card--empty">
            <p>Нет данных за выбранный период</p>
        </div>

        <Teleport to="body">
            <div v-if="showModal" class="record-modal-overlay" @click.self="showModal = false">
                <div class="record-modal" role="dialog">
                    <h3>Выберите показатель</h3>
                    <div class="record-modal__fields">
                        <UiSelect
                            v-model="modalElement"
                            label="Элемент"
                            :options="elementOptions"
                            :block="false"
                            align="left"
                            value-type="string"
                        />
                        <UiSelect
                            v-model="modalMetric"
                            label="Метрика"
                            :options="modalMetrics"
                            :block="false"
                            align="left"
                            value-type="string"
                        />
                    </div>
                    <div class="record-modal__actions">
                        <button type="button" class="btn-primary" @click="applyModal">Применить</button>
                        <button type="button" class="btn-secondary" @click="showModal = false">Отмена</button>
                    </div>
                </div>
            </div>
        </Teleport>
    </section>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { getPrimaryRecordKey } from '@/utils/playerStatsCalculator';
import UiSelect from '@/components/ui/forms/UiSelect.vue';

const props = defineProps({
    records: { type: Object, default: () => ({}) },
    position: { type: String, default: null },
});

const showModal = ref(false);
const elementOptions = [
    { value: 'total', label: 'Всего очков' },
    { value: 'attack', label: 'Атака' },
    { value: 'serve', label: 'Подача' },
    { value: 'block', label: 'Блок' },
    { value: 'reception', label: 'Приём' },
    { value: 'dig', label: 'Защита' },
    { value: 'set', label: 'Передача' },
];

const elementMetrics = {
    total: [{ value: 'points', label: 'Очки' }],
    attack: [
        { value: 'points', label: 'Очки' },
        { value: 'efficiency', label: 'Эффективность' },
    ],
    serve: [
        { value: 'aces', label: 'Эйсы' },
        { value: 'efficiency', label: 'Эффективность' },
    ],
    block: [
        { value: 'kills', label: 'Очки' },
        { value: 'efficiency', label: 'Эффективность' },
    ],
    reception: [
        { value: 'perfect', label: 'Отличный, %' },
        { value: 'positive', label: 'Позитивный, %' },
        { value: 'efficiency', label: 'Эффективность' },
    ],
    dig: [
        { value: 'success', label: 'Успешные' },
        { value: 'efficiency', label: 'Эффективность' },
    ],
    set: [{ value: 'efficiency', label: 'Эффективность' }],
};

const optionMap = {
    total: { points: 'total_points' },
    attack: { points: 'attack_points', efficiency: 'attack_efficiency' },
    serve: { aces: 'serve_aces', efficiency: 'serve_efficiency' },
    block: { kills: 'block', efficiency: 'block_efficiency' },
    reception: { perfect: 'reception_perfect', positive: 'reception', efficiency: 'reception_efficiency' },
    dig: { success: 'dig_success', efficiency: 'dig_efficiency' },
    set: { efficiency: 'set_efficiency' },
};

const metricLabels = {
    total_points: 'всего очков',
    attack_points: 'очков в атаке',
    attack_efficiency: 'эффективности атаки',
    serve_aces: 'эйсов',
    serve_efficiency: 'эффективности подачи',
    block: 'блоков',
    block_efficiency: 'эффективности блока',
    reception: 'позитивного приёма',
    reception_perfect: 'отличного приёма',
    reception_efficiency: 'эффективности приёма',
    dig_success: 'успешных защит',
    dig_efficiency: 'эффективности защиты',
    set_efficiency: 'эффективности передач',
};

const defaultKey = computed(() => getPrimaryRecordKey(props.position));

const selectedKey = ref(defaultKey.value);
const modalElement = ref('attack');
const modalMetric = ref('efficiency');

watch(defaultKey, (k) => {
    selectedKey.value = k;
});

const selectedOption = computed(() => selectedKey.value);

const currentRecord = computed(() => {
    const r = props.records[selectedOption.value];
    if (r) return r;
    return props.records.total_points || props.records.attack_efficiency || null;
});

const modalMetrics = computed(() => elementMetrics[modalElement.value] || []);

watch(modalElement, (el) => {
    const metrics = elementMetrics[el];
    if (metrics?.length) modalMetric.value = metrics[0].value;
});

const metricLabel = computed(() => metricLabels[selectedOption.value] || '');

const displayValue = computed(() => {
    const r = currentRecord.value;
    if (!r) return '—';
    if (
        selectedOption.value.includes('efficiency') ||
        selectedOption.value === 'reception' ||
        selectedOption.value === 'reception_perfect'
    ) {
        return `${r.value}%`;
    }
    return r.value;
});

const applyModal = () => {
    const key = optionMap[modalElement.value]?.[modalMetric.value];
    if (key) selectedKey.value = key;
    showModal.value = false;
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        const parts = dateString.split('.');
        if (parts.length !== 3) return dateString;
        const months = [
            'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
            'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
        ];
        return `${parseInt(parts[0], 10)} ${months[parseInt(parts[1], 10) - 1]} ${parseInt(parts[2], 10)}`;
    } catch {
        return dateString;
    }
};
</script>
